import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    EventEmitter,
    inject,
    Input,
    OnInit,
    Output,
    TemplateRef,
    ViewEncapsulation,
} from '@angular/core';
import { AppTextService } from '@fi-shared/app-text';
import { ISectionCardData } from '@fi-shared/fi-section-card/fi-section-card';
import {
    IAccessRightCoreFilter,
    IAccessRightFilterCollection,
    IAccessRightSelectionFiltersCollection,
    IAccessRightUserFilter,
    ILightWeightBusinessUnit,
} from '@fi-shared/models';
import { FiChecklist2ScheduleSharingDisplayService } from '@fi-shared/checklist/schedule/services/fi-checklist2-schedule-sharing-display.service';
import { ModuleType, ObjectTypes } from '@fi-shared/types';
import { ValidationErrors } from '@angular/forms';
import { IMergeLightWeightObjectsResult, mergeLightWeightObjects } from '@fi-shared/utils/merge-light-weight-objects';
import { FiUserDataService } from '@fi-shared/fi-shared-services/fi-user-data.service';
import { FiComponentsContainer } from '@fi-shared/fi-components-container';
import { cloneDeep, isDefined } from '@fi-shared/utils';
import { FiAccessRightHelperService } from '@fi-shared/fi-shared-services/fi-access-right-helper.service';
import { SaveChangesWarningService } from '@fi-shared/fi-shared-services/save-changes-warning.service';
import { FiChecklistHelperService } from '@fi-shared/checklist/fi-checklist-helper.service';
import { IItemSharingSettingsDialogResult } from '@fi-shared/access-right-filters/fi-item-sharing-settings-dialog/fi-item-sharing-settings';
import { FiItemSharingSettingsDialogService } from '@fi-shared/access-right-filters/fi-item-sharing-settings-dialog/fi-item-sharing-settings.service';

@Component({
    selector: 'fi-checklist2-schedule-edit-unit-assignment',
    templateUrl: './unit-assignment.component.html',
    styleUrls: ['./unit-assignment.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    host: { class: 'fi-checklist2-schedule-edit-unit-assignment' },
    providers: [{ provide: FiComponentsContainer, useExisting: FiChecklist2ScheduleEditUnitAssignmentComponent }],
    standalone: false,
})
export class FiChecklist2ScheduleEditUnitAssignmentComponent extends FiComponentsContainer implements OnInit {
    @Input({ required: true }) accessRightCoreFilter: IAccessRightCoreFilter;
    @Input({ required: true }) accessRightUserFilter: IAccessRightUserFilter;
    @Output() accessRightCoreFilterChange = new EventEmitter<IAccessRightCoreFilter>();
    @Input() savedQuickSelectionFilters: IAccessRightSelectionFiltersCollection[] = [];

    @Input({ required: true }) availableAccessRightFilterCollection: IAccessRightFilterCollection;
    @Input() checklistScheduleSettingsSharingInfoContainer: TemplateRef<any>;
    @Input() isOperationalSchedule = false;
    @Input() createdByUserId: number;
    @Input({ required: true }) auditMode = false;
    @Input() scrollableElementRefs: ElementRef[];

    protected unitAreaAssignmentContentCardData: ISectionCardData;
    private businessUnitIdsOutOfScope: number[] = [];

    private appTextService = inject(AppTextService);
    private fiChecklist2ScheduleSharingDisplayService = inject(FiChecklist2ScheduleSharingDisplayService);
    private cdr = inject(ChangeDetectorRef);
    private fiUserDataService = inject(FiUserDataService);
    private fiAccessRightHelperService = inject(FiAccessRightHelperService);
    private saveChangesWarningService = inject(SaveChangesWarningService);
    private fiChecklist2HelperService = inject(FiChecklistHelperService);
    private fiItemSharingSettingsDialogService = inject(FiItemSharingSettingsDialogService);

    public ngOnInit(): void {
        this.setContentCardsData();
        this.getAccessRightsForPublication();
    }

    private setContentCardsData(): void {
        this.unitAreaAssignmentContentCardData = {
            headerTextLabel: this.appTextService.get('unit.unit_assignment'),
            subheaderTextLabel: this.appTextService.get('unit.unit_assignment_description'),
            id: undefined,
        };
    }

    private getAccessRightsForPublication(): void {
        const mergeResult: IMergeLightWeightObjectsResult<ILightWeightBusinessUnit> = mergeLightWeightObjects(
            this.availableAccessRightFilterCollection.AccessRightCoreFilter.BusinessUnits,
            this.accessRightCoreFilter.BusinessUnits,
        );

        this.businessUnitIdsOutOfScope = mergeResult.disabledItemIds;
        this.availableAccessRightFilterCollection.AccessRightCoreFilter.BusinessUnits = mergeResult.mergedResult;
        this.initChecklistDefaultSharingSettings();
    }

    private selectedFiltersCollection: IAccessRightSelectionFiltersCollection;
    private setSelectedFiltersCollection(): void {
        this.selectedFiltersCollection = this.fiItemSharingSettingsDialogService.getSavedFilterBySharingSelection(
            this.accessRightCoreFilter,
            this.savedQuickSelectionFilters,
        );
    }

    private initChecklistDefaultSharingSettings(): void {
        this.setSelectedFiltersCollection();
        if (isDefined(this.selectedFiltersCollection)) {
            return;
        }

        // Set as default feature is disabled https://chainformation.atlassian.net/browse/FI-6357?focusedCommentId=62247
        /*if (!this.createdByUserId) {
            const defaultFilterSelection = this.savedQuickSelectionFilters
                .find(({ IsDefault }) => IsDefault);

            if (isDefined(defaultFilterSelection)) {
                this.selectedFiltersCollection = defaultFilterSelection;
                this.accessRightCoreFilter = cloneDeep(defaultFilterSelection.SelectionFilterSettingCollection.AccessRightCoreFilter);
                this.accessRightCoreFilterChange.emit(this.accessRightCoreFilter);
            }
        }*/
    }

    protected editChecklistUnitAssignment(): void {
        this.fiItemSharingSettingsDialogService
            .openItemSharingSettingsDialog(
                {
                    businessUnitIdsOutOfScope: this.businessUnitIdsOutOfScope,
                    isShared: true,
                    showSharingSelection: false,
                    selectedFiltersCollection: this.selectedFiltersCollection,
                    savedQuickSelectionFilters: this.savedQuickSelectionFilters,
                    isItemOwner: this.createdByUserId === this.fiUserDataService.userData.UserId,
                    availableAccessRightFilterCollection: this.availableAccessRightFilterCollection,
                    itemAccessRightCoreFilter: cloneDeep(this.accessRightCoreFilter),
                    itemAccessRightUserFilter: this.accessRightUserFilter,
                    dialogDescriptionText: this.appTextService.get('checklist.schedule_sharing_selection_description'),
                    individualUnitSectionDescriptionText: this.appTextService.get(
                        'checklist.schedule_individual_units_description',
                    ),
                    auditMode: this.auditMode,
                    moduleType: this.auditMode ? ModuleType.ChecklistV2Audit : ModuleType.ChecklistV2,
                },
                this.appTextService.get('unit.unit_assignment'),
            )
            .onYesButtonClick((result: IItemSharingSettingsDialogResult) => {
                const resultScheduleAccessRightCoreFilter: IAccessRightCoreFilter = cloneDeep(
                    result.accessRightCoreFilter,
                );
                delete resultScheduleAccessRightCoreFilter.Clusters;
                delete resultScheduleAccessRightCoreFilter.ClusterIds;
                delete resultScheduleAccessRightCoreFilter.Roles;
                delete resultScheduleAccessRightCoreFilter.RoleIds;
                this.accessRightCoreFilter = { ...this.accessRightCoreFilter, ...resultScheduleAccessRightCoreFilter };
                this.accessRightCoreFilterChange.emit(this.accessRightCoreFilter);
                this.cdr.markForCheck();
            })
            .afterClosed((result) => {
                this.savedQuickSelectionFilters = cloneDeep(result.value.savedQuickSelectionFilters);
                this.setSelectedFiltersCollection();
                this.saveChangesWarningService.closeDialogOnTransition = false;
            });

        this.saveChangesWarningService.closeDialogOnTransition = true;
    }

    protected validateUnitAssigmentFn = (): ValidationErrors => {
        const errors = this.fiAccessRightHelperService.validateAccessRightsCoreFilters(this.accessRightCoreFilter, {
            isShared: this.isOperationalSchedule,
            skipRolesCheck: true,
        });
        return errors;
    };

    protected getSelectedAreasBusinessAreasNames(): string {
        return this.fiChecklist2ScheduleSharingDisplayService.getSelectedAreasBusinessAreasNames(
            this.accessRightCoreFilter,
            this.availableAccessRightFilterCollection.AccessRightCoreFilter,
        );
    }

    protected getSelectedBusinessUnitsNames(): string {
        return this.fiChecklist2ScheduleSharingDisplayService.getSelectedSharingItemsNamesByObjectType(
            ObjectTypes.BusinessUnit,
            this.accessRightCoreFilter,
            this.availableAccessRightFilterCollection.AccessRightCoreFilter,
        );
    }
}
